import { CalculatedRate, Ratelist } from "@sinch/types/sinch.types";
import { isNotNil } from "ramda-adjunct";
import * as React from "react";
import { ReactNode } from "react";
import { Row } from "@sinch/components/application/RatesCompositionTable/RatesCompositionTable";
import { MaterCategory } from "@sinch/components/application/RatesCompositionTable/types";
import { prop } from "ramda";
import { ExtendedRateComposition } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useTreeQueries";

export type EntityName = "Order" | "Job" | "Shift" | "Position" | "Attendance";

export type TableItemsSource<
  TComposition extends {
    total: number;
    components: { total: number; component: string; ratelistId?: string | null; calculatedRates?: CalculatedRate[] }[];
  } = {
    total: number;
    components: { total: number; component: string; ratelistId?: string | null; calculatedRates?: CalculatedRate[] }[];
  },
> = {
  id: string;
  wageComposition?: Omit<TComposition, "__typename"> | null;
  priceComposition?: Omit<TComposition, "__typename"> | null;
  wageRatelist?: Pick<Ratelist, "id" | "name" | "type"> | null;
  priceRatelist?: Pick<Ratelist, "id" | "name" | "type"> | null;
  overriddenRatelists?: Pick<Ratelist, "id" | "name" | "type">[];

  [k: string]: unknown;
};
export interface ChildrenGetterResult {
  childrenList: TableItemsSource<ExtendedRateComposition>[];
  childrenEntityName: EntityName;
  childrenBuildRowResolver?: BuildEntityRowFn;
}

export type BuildEntityRowFn<
  TComposition extends { total: number; components: { total: number; component: string }[] } = {
    total: number;
    components: { total: number; component: string }[];
  },
> = (args: {
  items: TableItemsSource<TComposition>[];
  entityName: EntityName | MaterCategory;
  parent: string[];
  labelGetter?: LabelGetter;
  childrenGetter: ChildrenGetter;
}) => BuildEntityRowResult;

interface BuildEntityRowResult {
  rows: Row[];
}

export type LabelGetter = (data: Record<string, unknown>, entityName: EntityName) => ReactNode;
export type ChildrenGetter = (data: Record<string, unknown>, entityName: EntityName) => ChildrenGetterResult;
const createEntityId = (entityName: string, id: string): string => `${entityName}:${id}`;

export const buildEntityRow: BuildEntityRowFn<ExtendedRateComposition> = ({
  items,
  entityName,
  parent = [],
  childrenGetter,
  labelGetter,
}) => {
  const rows = items.flatMap((entityData): Row[] => {
    const overriddenRatelistIds = new Map((entityData.overriddenRatelists ?? []).map(({ id, ...rest }) => [id, rest]));

    const { id } = entityData;
    const entityId = createEntityId(entityName, id);
    const currentPath = [...parent, entityId];

    const { childrenList, childrenEntityName, childrenBuildRowResolver } = childrenGetter(
      entityData as Record<string, unknown>,
      entityName as EntityName
    );

    const { rows: children } = (childrenBuildRowResolver ?? buildEntityRow)({
      items: childrenList ?? [],
      entityName: childrenEntityName,
      parent: currentPath,
      childrenGetter,
      labelGetter,
    });

    const parentRow: Row = {
      entityName: entityName as EntityName,
      id: currentPath.join("/"),
      path: currentPath,
      label: labelGetter?.(entityData as Record<string, unknown>, entityName as EntityName) ?? (
        <div>
          {entityName} {id}
        </div>
      ),
      price: entityData.priceComposition?.total ?? 0,
      wage: entityData.wageComposition?.total ?? 0,
      total: (entityData.priceComposition?.total ?? 0) - (entityData.wageComposition?.total ?? 0),
      priceDetails: [entityData.priceComposition].filter(isNotNil).map((rateComp) => ({
        total: rateComp?.total ?? 0,
        components:
          rateComp?.components.map((item) => ({
            ...item,
            overridden: prop("ratelistId", item) != null && overriddenRatelistIds.get(item.ratelistId!),
          })) ?? [],
      })), //childrenPriceRates,
      wageDetails: [entityData.wageComposition].filter(isNotNil).map((rateComp) => ({
        total: rateComp?.total ?? 0,
        components:
          rateComp?.components.map((item) => ({
            ...item,
            overridden: prop("ratelistId", item) != null && overriddenRatelistIds.get(item.ratelistId!),
          })) ?? [],
      })),
    };
    return [parentRow, ...children];
  });

  return { rows };
};
